import {
  DocumentExtension,
  DocumentReferenceEntityType,
} from '@qtek/shared/models';

export interface DocumentServingUrlParams {
  baseUrl?: string;
  /** Paired with `refId` into the `"<type>=<id>"` query value the backend expects. Omit when `source: 'resource'` — that case has no entity type. */
  referenceType?: DocumentReferenceEntityType;
  /**
   * The DocumentReference row's own `_id` — NOT the document id or the
   * owning-entity id. When `source: 'resource'`, this is instead a
   * tilde-encoded classpath path and is sent as-is, with no `<type>=` prefix.
   */
  referenceId?: string;
  /** Thumbnail size, PDF preview, or extracted text. Omit for the original file. */
  extension?: DocumentExtension;
  /** 'resource' serves a static classpath asset instead of S3 — in that case `refId` is a tilde-encoded classpath path, not a typed reference id. */
  source?: 'resource';
  /** JWT granting temporary access. */
  token?: string;
  /**
   * Client-only cache-bust value, appended as `t=`. Not a real backend
   * param — just consolidates the 3 different cache-buster keys (`t`/`_t`/
   * `retry`) already scattered across the app into one.
   */
  cacheBust?: string | number;
  /** Hits `GET /api/v2/documents/download` (forces a file download) instead of the preview route. */
  download?: boolean;
}

/**
 * Builds a `GET /api/v2/documents` serving URL (preview/thumbnail/download)
 * — the replacement for the legacy `/api/v1/service/doc/{docId}` family.
 * Every param is optional so this can be used for partial/progressive
 * construction; the backend does require `refId` outside the
 * `src=resource` case, so omitting it yields a base URL with no query.
 */
export function buildDocumentServingUrl(
  params: DocumentServingUrlParams
): string {
  const search = new URLSearchParams();

  if (params.source === 'resource' && params.referenceId) {
    search.set('referenceId', params.referenceId);
  } else if (params.referenceType && params.referenceId) {
    search.set('referenceId', `${params.referenceType}=${params.referenceId}`);
  }
  if (params.extension) {
    search.set('extension', params.extension);
  }
  if (params.source) {
    search.set('source', params.source);
  }
  if (params.token) {
    search.set('token', params.token);
  }
  if (params.cacheBust !== undefined) {
    search.set('t', String(params.cacheBust));
  }

  const query = search.toString();
  const path = params.download
    ? '/api/v2/documents/download'
    : '/api/v2/documents';
  return `${params.baseUrl ?? ''}${path}${query ? `?${query}` : ''}`;
}
